import {
  CubicBezierTuple,
  FilterType,
  IEffectsEntryAnimation,
  IFilterItem,
  ITransformItem,
  ITransitionItem,
  TransitionTimingFunction
} from "@kuika/kuika-cl-model";
import { v4 as uuidv4 } from "uuid";
import type { KuikaSelectboxOption, KuikaSelectboxOptionGroup } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";

/**
 * Summary strings shown in TransformAccordion rows and PopoverInput
 * read-only inputs. Kept in one helpers module so that designer canvas
 * preview (apply-css-styles) and Vitest pure tests can share them.
 */
export const summarizeTransform = (item: ITransformItem): string => {
  const x: number = item.x ?? 0;
  const y: number = item.y ?? 0;
  const z: number = item.z ?? 0;
  switch (item.type) {
    case "move":
      return `Move: ${x}px, ${y}px, ${z}px`;
    case "scale":
      return `Scale: ${x}, ${y}, ${z}`;
    case "rotate":
      return `Rotate: ${x}deg, ${y}deg, ${z}deg`;
    case "skew":
      return `Skew: ${x}deg, ${y}deg`;
    default:
      return String(item.type);
  }
};

export const summarizeTransition = (item: ITransitionItem): string => {
  const parts: string[] = [];
  parts.push(item.property || "all");
  parts.push(`${item.duration ?? 200}ms`);
  if (item.timingFunction === "cubic-bezier") {
    parts.push("cubic-bezier");
  } else {
    parts.push(item.timingFunction ?? "ease");
  }
  if (item.delay !== undefined && item.delay > 0) {
    parts.push(`+${item.delay}ms`);
  }
  return parts.join(" ");
};

/** 5 default easing presets — custom cubic-bezier curve editor is out of scope. */
export const TRANSITION_EASING_OPTIONS: KuikaSelectboxOption[] = [
  { value: "linear", label: "Linear" },
  { value: "ease", label: "Ease" },
  { value: "ease-in", label: "Ease In" },
  { value: "ease-out", label: "Ease Out" },
  { value: "ease-in-out", label: "Ease In Out" }
];

export interface TransitionPreset {
  name: string;
  duration: number;
  timingFunction: TransitionTimingFunction;
  cubicBezier?: CubicBezierTuple;
}

/**
 * 6 presets. Spring-like is the only preset that emits a cubic-bezier; users
 * can pick it from this dropdown but cannot edit the bezier numbers (custom
 * curve editor is a Faz 5+ candidate).
 */
export const TRANSITION_PRESETS: TransitionPreset[] = [
  { name: "Instant", duration: 0, timingFunction: "linear" },
  { name: "Fast", duration: 120, timingFunction: "ease-out" },
  { name: "Smooth", duration: 200, timingFunction: "ease" },
  { name: "Soft", duration: 300, timingFunction: "ease-in-out" },
  { name: "Slow", duration: 500, timingFunction: "ease-in-out" },
  { name: "Spring-like", duration: 400, timingFunction: "cubic-bezier", cubicBezier: [0.34, 1.56, 0.64, 1] }
];

export const TRANSITION_PRESET_OPTIONS: KuikaSelectboxOption[] = TRANSITION_PRESETS.map(
  (p: TransitionPreset): KuikaSelectboxOption => ({ value: p.name, label: p.name })
);

/**
 * Apply a preset patch to a transition item. The preset overrides duration,
 * timingFunction, and (for Spring-like) cubicBezier; everything else (property,
 * delay, id) is preserved.
 */
export const applyTransitionPreset = (item: ITransitionItem, presetName: string): ITransitionItem => {
  const preset: TransitionPreset | undefined = TRANSITION_PRESETS.find(
    (p: TransitionPreset): boolean => p.name === presetName
  );
  if (!preset) return item;
  const next: ITransitionItem = {
    ...item,
    duration: preset.duration,
    timingFunction: preset.timingFunction
  };
  if (preset.cubicBezier) {
    next.cubicBezier = preset.cubicBezier;
  } else {
    next.cubicBezier = undefined;
  }
  return next;
};

/**
 * Entry / mount animation helpers. The Effects panel exposes a single
 * `entryAnimation` per component (Fade / Slide / Zoom / Rotate In) — the
 * generator turns it into real `@keyframes`. The user picks a *type* (the
 * common motion presets) + duration / easing / delay; the type maps to a
 * "from" offset, mirroring the reveal presets so a preset-applied animation
 * shows the right type here. Shared with the manual control and Vitest tests.
 */
export type EntryAnimationType =
  | "none"
  | "fade"
  | "slide-up"
  | "slide-down"
  | "slide-left"
  | "slide-right"
  | "zoom"
  | "rotate";

interface EntryAnimationFromOffset {
  fromOpacity?: number;
  fromTranslateX?: number;
  fromTranslateY?: number;
  fromScale?: number;
  fromRotateZ?: number;
}

/** From-offset per entry type — aligned with the reveal preset `initialStateHint`s. */
const ENTRY_FROM_BY_TYPE: Record<Exclude<EntryAnimationType, "none">, EntryAnimationFromOffset> = {
  fade: { fromOpacity: 0 },
  "slide-up": { fromOpacity: 0, fromTranslateY: 16 },
  "slide-down": { fromOpacity: 0, fromTranslateY: -16 },
  "slide-left": { fromOpacity: 0, fromTranslateX: 24 },
  "slide-right": { fromOpacity: 0, fromTranslateX: -24 },
  zoom: { fromOpacity: 0, fromScale: 0.94 },
  rotate: { fromOpacity: 0, fromScale: 0.96, fromRotateZ: -8 }
};

export const ENTRY_ANIMATION_TYPE_OPTIONS: KuikaSelectboxOption[] = [
  { value: "none", label: "None" },
  { value: "fade", label: "Fade In" },
  { value: "slide-up", label: "Slide Up" },
  { value: "slide-down", label: "Slide Down" },
  { value: "slide-left", label: "Slide Left" },
  { value: "slide-right", label: "Slide Right" },
  { value: "zoom", label: "Zoom In" },
  { value: "rotate", label: "Rotate In" }
];

// Mount/entry animations read best slower than hover micro-interactions —
// 200-300ms feels rushed for a reveal. 500ms ease-out is the graceful default
// for both preset-derived and manually-configured entry animations.
export const ENTRY_ANIMATION_DEFAULT_DURATION: number = 500;
export const ENTRY_ANIMATION_DEFAULT_EASING: string = "ease-out";

/** Build an entryAnimation for a chosen type + timing. `none` clears it (null). */
export const buildEntryAnimationFromType = (
  type: EntryAnimationType,
  durationMs: number,
  timingFunction: string,
  delayMs: number
): IEffectsEntryAnimation | null => {
  if (type === "none") return null;
  const offset: EntryAnimationFromOffset = ENTRY_FROM_BY_TYPE[type];
  return { ...offset, durationMs, timingFunction, delayMs };
};

/**
 * Reverse-infer the type from a stored entryAnimation so the dropdown can show
 * it. Checks the most specific offset first (rotate carries scale + rotateZ).
 * Magnitudes don't matter — only which axes are present + their sign.
 */
export const detectEntryAnimationType = (anim: IEffectsEntryAnimation | null | undefined): EntryAnimationType => {
  if (!anim) return "none";
  if (anim.fromRotateZ !== undefined && anim.fromRotateZ !== null) return "rotate";
  if (anim.fromScale !== undefined && anim.fromScale !== null) return "zoom";
  if (anim.fromTranslateX !== undefined && anim.fromTranslateX !== null) {
    return anim.fromTranslateX >= 0 ? "slide-left" : "slide-right";
  }
  if (anim.fromTranslateY !== undefined && anim.fromTranslateY !== null) {
    return anim.fromTranslateY >= 0 ? "slide-up" : "slide-down";
  }
  if (anim.fromOpacity !== undefined && anim.fromOpacity !== null) return "fade";
  return "none";
};

/** Read-only timing summary shown in the entry-animation PopoverInput. */
export const summarizeEntryAnimationTiming = (anim: IEffectsEntryAnimation): string => {
  const parts: string[] = [
    `${anim.durationMs ?? ENTRY_ANIMATION_DEFAULT_DURATION}ms`,
    anim.timingFunction ?? ENTRY_ANIMATION_DEFAULT_EASING
  ];
  if (anim.delayMs !== undefined && anim.delayMs > 0) {
    parts.push(`+${anim.delayMs}ms`);
  }
  return parts.join(" · ");
};

/**
 * Faz 4 — Filter helpers (summary string, defaults, type groups). Shared
 * between FilterPopoverContent (popover editor) and EffectsFiltersComponent
 * (row list), and reused by Vitest pure tests.
 */
export const summarizeFilter = (item: IFilterItem): string => {
  switch (item.type) {
    case "blur":
      return `blur(${item.value ?? 0}px)`;
    case "brightness":
    case "contrast":
    case "saturate":
      return `${item.type}(${item.value ?? 1})`;
    case "grayscale":
    case "invert":
    case "sepia":
      return `${item.type}(${item.value ?? 100}%)`;
    case "hue-rotate":
      return `hue-rotate(${item.value ?? 0}deg)`;
    case "drop-shadow":
      return `drop-shadow(${item.offsetX ?? 0}px ${item.offsetY ?? 0}px ${item.blur ?? 0}px)`;
    default:
      return String(item.type);
  }
};

/**
 * Build a fresh filter item for a given type with sensible defaults. Called
 * when the user picks a type from the dropdown (existing value is discarded
 * to avoid stale numeric values bleeding across incompatible units — a
 * `100`% grayscale should not become `100`px blur on type change).
 */
export const defaultFilterItem = (type: FilterType): IFilterItem => {
  switch (type) {
    case "blur":
      return { id: uuidv4(), type, value: 4, unit: "px" };
    case "brightness":
    case "contrast":
    case "saturate":
      return { id: uuidv4(), type, value: 1.0, unit: "none" };
    case "grayscale":
    case "invert":
    case "sepia":
      return { id: uuidv4(), type, value: 100, unit: "%" };
    case "hue-rotate":
      return { id: uuidv4(), type, value: 0, unit: "deg" };
    case "drop-shadow":
      // drop-shadow uses flat offset/blur/color fields instead of value/unit.
      return { id: uuidv4(), type, offsetX: 0, offsetY: 4, blur: 8, color: "rgba(0,0,0,0.35)" };
    default:
      return { id: uuidv4(), type, value: 0, unit: "none" };
  }
};

/**
 * Filter type selector base groups (Lens & Light + Color). drop-shadow is NOT
 * listed here — it is appended only for the `filter` section via
 * getFilterTypeGroups(true). drop-shadow follows the element's shape (alpha
 * mask) and is distinct from the rectangular box-shadow handled by the Shadow
 * panel; it is omitted from `backdrop-filter` because backdrop drop-shadow has
 * no useful visual effect.
 */
export const FILTER_TYPE_GROUPS: KuikaSelectboxOptionGroup[] = [
  {
    label: "Lens & Light",
    options: [
      { value: "blur", label: "Blur" },
      { value: "brightness", label: "Brightness" },
      { value: "contrast", label: "Contrast" }
    ]
  },
  {
    label: "Color",
    options: [
      { value: "saturate", label: "Saturation" },
      { value: "grayscale", label: "Grayscale" },
      { value: "invert", label: "Invert" },
      { value: "sepia", label: "Sepia" },
      { value: "hue-rotate", label: "Hue Rotate" }
    ]
  }
];

/** Extra "Shadow" group appended only for the `filter` section. */
const DROP_SHADOW_GROUP: KuikaSelectboxOptionGroup = {
  label: "Shadow",
  options: [{ value: "drop-shadow", label: "Drop Shadow" }]
};

/**
 * Type-group list for the filter type selectbox. `filter` allows drop-shadow
 * (shape-following shadow for icons / transparent images / text); `backdrop-
 * filter` does not (no useful visual effect on the backdrop).
 */
export const getFilterTypeGroups = (allowDropShadow: boolean): KuikaSelectboxOptionGroup[] =>
  allowDropShadow ? [...FILTER_TYPE_GROUPS, DROP_SHADOW_GROUP] : FILTER_TYPE_GROUPS;

/**
 * Transition Property selectbox groups. Only properties that Kuika actually
 * supports in its style model (position, typography, fill, border, shadow,
 * effects). Webflow-listed properties that don't exist on the Kuika side
 * (e.g. `background-image-position`) are dropped.
 */
export const TRANSITION_PROPERTY_GROUPS: KuikaSelectboxOptionGroup[] = [
  {
    label: "Common",
    options: [
      { value: "opacity", label: "Opacity" },
      { value: "margin", label: "Margin" },
      { value: "padding", label: "Padding" },
      { value: "border", label: "Border" },
      { value: "transform", label: "Transform" },
      { value: "filter", label: "Filter" },
      { value: "flex", label: "Flex" }
    ]
  },
  {
    label: "Background",
    options: [
      { value: "background-color", label: "Background Color" },
      { value: "background-position", label: "Background Position" },
      { value: "text-shadow", label: "Text Shadow" },
      { value: "box-shadow", label: "Box Shadow" }
    ]
  },
  {
    label: "Size",
    options: [
      { value: "width", label: "Width" },
      { value: "height", label: "Height" },
      { value: "max-height", label: "Max Height" },
      { value: "max-width", label: "Max Width" },
      { value: "min-height", label: "Min Height" },
      { value: "min-width", label: "Min Width" }
    ]
  },
  {
    label: "Borders",
    options: [
      { value: "border-radius", label: "Border Radius" },
      { value: "border-color", label: "Border Color" },
      { value: "border-width", label: "Border Width" },
      // Outline lives in the Borders group (no separate "Outline" category, so the
      // group count stays at 9). These enable animated focus/selection rings —
      // the Outline section already edits these statically; transitioning them
      // smooths the ring on hover/focus. outline-style is intentionally omitted
      // (discrete: solid↔dashed can't tween). Codegen emits the property verbatim
      // (StyleHelper.buildTransitionInlineStyle / EffectsCssBuilder.AppendTransitionEntry).
      { value: "outline-color", label: "Outline Color" },
      { value: "outline-offset", label: "Outline Offset" },
      { value: "outline-width", label: "Outline Width" }
    ]
  },
  {
    label: "Typography",
    options: [
      { value: "color", label: "Font Color" },
      { value: "font-size", label: "Font Size" },
      { value: "line-height", label: "Line Height" },
      { value: "letter-spacing", label: "Letter Spacing" },
      { value: "text-indent", label: "Text Indent" },
      { value: "word-spacing", label: "Word Spacing" },
      { value: "font-variation-settings", label: "Font Variations" },
      { value: "-webkit-text-stroke-color", label: "Text Stroke Color" },
      { value: "text-underline-offset", label: "Text Underline Offset" },
      { value: "text-decoration-color", label: "Text Decoration Color" }
    ]
  },
  {
    label: "Position",
    options: [
      { value: "top", label: "Top" },
      { value: "left", label: "Left" },
      { value: "bottom", label: "Bottom" },
      { value: "right", label: "Right" },
      { value: "z-index", label: "z-Index" }
    ]
  },
  {
    label: "Margin",
    options: [
      { value: "margin-top", label: "Margin Top" },
      { value: "margin-right", label: "Margin Right" },
      { value: "margin-bottom", label: "Margin Bottom" },
      { value: "margin-left", label: "Margin Left" }
    ]
  },
  {
    label: "Padding",
    options: [
      { value: "padding-top", label: "Padding Top" },
      { value: "padding-right", label: "Padding Right" },
      { value: "padding-bottom", label: "Padding Bottom" },
      { value: "padding-left", label: "Padding Left" }
    ]
  },
  {
    label: "Flex",
    options: [
      { value: "flex-grow", label: "Flex Grow" },
      { value: "flex-shrink", label: "Flex Shrink" },
      { value: "flex-basis", label: "Flex Basis" }
    ]
  }
];
